import { mdiChevronDown, mdiChevronUp } from '@mdi/js';
import Icon from '@mdi/react';
import clsx from 'clsx';
import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import useStoreSlots from '../../../hooks/useStoreSlots';
import useWizardContext from '../../../hooks/useWizardContext';
import { Store } from '../../../types/Store';
import { formatDate, formatTime } from '../../../utils/formatDateAndTime';
import showCallcenterHint from '../../../utils/showCallcenterHint';
import classes from '../classes.module.css';
import usePromotions from '../../../hooks/usePromotions';
import StoreTileNoTimeslot from './StoreTileNoTimeslot';
import StoreTimeslots from './StoreTimeslots';

const StoreTile: React.FC<{
  isOpen: boolean;
  setStores: React.Dispatch<React.SetStateAction<Store[] | null>>;
  setUpdated: React.Dispatch<React.SetStateAction<boolean>>;
  store: Store;
  stores: Store[];
  toggleOpen: () => void;

}> = ({ isOpen, toggleOpen, store, setStores, stores, setUpdated }) => {
  const intl = useIntl();
  const ctx = useWizardContext();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars

  //const [slotsByDateSource, slotsAreLoading] = useStoreSlots(ctx.appointmentType?.id, store.shopNumber, 90);

  //we now eventually take the appointment type id from the store (before, we always took it from the selection on first screen)
  //this is only the case if the user has chosen appointment type "Adviesgesprek (30 minuten)", which has appointment type id "35f42b66-7450-4df4-9226-63a49d9cde78"
  const appointmentTypeIdToBeTaken =
    ctx.appointmentType?.id === '35f42b66-7450-4df4-9226-63a49d9cde78'
      ? store.appointmentTypeId
      : ctx.appointmentType?.id;
  let limit = 90;
  if (appointmentTypeIdToBeTaken == '9b0ff9f0-29bf-4653-9d23-e875346bbb9a') {
    limit = 14;
  }
  const [slotsByDateSource, slotsAreLoading] = useStoreSlots(appointmentTypeIdToBeTaken, store.shopNumber, limit);

  const promotions = usePromotions();
  const slotsByDate = useMemo(() => {
    if (slotsByDateSource == null || !promotions) return slotsByDateSource;
    const promotionDays = store.promotionDays ? store.promotionDays.map((pd) => new Date(pd)) : [];
    return Object.entries(slotsByDateSource).reduce((res, [key, val]) => {
      const currDate = new Date(key);
      if (
        promotionDays.every(
          (pd) =>
            currDate.getFullYear() !== pd.getFullYear() ||
            currDate.getMonth() !== pd.getMonth() ||
            currDate.getDate() !== pd.getDate(),
        )
      )
        return res;
      return { ...res, [key]: val };
    }, {});
  }, [promotions, slotsByDateSource, store.promotionDays]);

  const firstSlot = (slotsByDate && (Object.values(slotsByDate)[0] || [])[0]) || null;

  useEffect(()=>{
  if (firstSlot) {
    const updatedStores = [...stores].map((s) => {
      if (s.shopNumber == store.shopNumber){
        s.firstAppointmentTimeslot = firstSlot;
        return s;
      } else {
        return s;
      }
    })
    setStores(updatedStores);
    setUpdated(true);
  }
  }, [firstSlot])



  const timeInDaysForCallcenterHint = (window as any).timeInDaysForCallcenterHint;

  return firstSlot || slotsAreLoading ? (
    <div className={classes.boxWrapper}>
      <div className={classes.box}>
        <button
          type='button'
          className={clsx(classes.openButtonMobile, !firstSlot ? classes.disabled : '')}
          onClick={toggleOpen}
        >
          {intl.formatMessage({ id: 'store.select' })}
          <div className={classes.iconWrapper}>
            {isOpen ? <Icon path={mdiChevronUp} size='25px' /> : <Icon path={mdiChevronDown} size='25px' />}
          </div>
          <p>{intl.formatMessage({ id: 'store.select.subtextbutton' })}</p>
        </button>
        <div className={classes.left}>
          <div>
            <h3 style={{ margin: '0px' }}>{store.name}</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35em', marginTop: '0.35em' }}>
            <span>{store.street}</span>
            <span>{store.city}</span>
            <span>{store.postalCode}</span>
          </div>
        </div>

        {/* In case the first possible appointment is more then 'timeInDaysForCallcenterHint' days in the future, display a hint to contact call center */}
        {firstSlot && showCallcenterHint(timeInDaysForCallcenterHint, firstSlot?.startDateTime) ? (
          <div style={{ marginBottom: '0.95em', marginLeft: '2em', marginRight: '2em' }}>
            <span>{intl.formatMessage({ id: 'contactCallcenter' })}</span>
          </div>
        ) : null}

        <div className={classes.right}>
          {/* <div className={classes.distance}>
            <h3 style={{ margin: '0px' }}>{store.distanceKm} km</h3>
          </div> */}
          {firstSlot ? (
            <div className={classes.timeslotInfo}>
              <span>{intl.formatMessage({ id: 'store.firstTimeslot' })}</span>
              <span>{formatDate(intl.locale, new Date(firstSlot.startDateTime))}</span>
              <span>
                <b>{formatTime(intl.locale, firstSlot.startDateTime)}</b>
              </span>
            </div>
          ) : null}
          {slotsByDate && Object.keys(slotsByDate).length === 0 ? (
            <div className={classes.timeslotInfo}> {intl.formatMessage({ id: 'store.noTimeSlot' })}</div>
          ) : null}
          <button
            type='button'
            className={clsx(classes.openButton, !firstSlot ? classes.disabled : '')}
            disabled={!firstSlot}
            onClick={toggleOpen}
          >
            {intl.formatMessage({ id: 'store.select' })}
            <div className={classes.iconWrapper}>
              {isOpen ? <Icon path={mdiChevronUp} size='25px' /> : <Icon path={mdiChevronDown} size='25px' />}
            </div>
            <p>{intl.formatMessage({ id: 'store.select.subtextbutton' })}</p>
          </button>
        </div>
      </div>
      {isOpen && slotsByDate && Object.keys(slotsByDate).length > 0 ? (
        <div className={classes.collapsibleTimeslots}>
          <StoreTimeslots slotsByDate={slotsByDate} store={store} />
        </div>
      ) : null}
    </div>
  ) : (
    <StoreTileNoTimeslot store={store} />
  );
};

StoreTile.displayName = 'StoreTile';

export default StoreTile;
